/* state/kommunkort-data.js — lazy-loader för Kommunkortets per-kommun-innehåll.
 *
 * Två gitignorade shared-data-filer (byggs av pipeline, servas från VPS
 * shared-mappen precis som kommun_wiki):
 *   /data/_generated/kommunkort/content.json
 *     { kod: { landskap, lead, vibe:[...], experiences:[{layer,note}],
 *              lead_en, vibe_en:[...] } }
 *   /data/_generated/kommunkort/photos.json
 *     { kod: [ { file, file_sm, file_1x, caption, caption_en, credit, … } ] }
 *     `file_sm` = 480 px galleri-variant, `file_1x` = 640 px hero-variant
 *     (båda ur build_kommunkort_thumbs.py). Galleriet ERSÄTTER originalet
 *     med `file_sm`; hero KOMPLETTERAR det med `file_1x` i en srcset.
 *     Saknas fälten faller anroparen tillbaka på `url`.
 *     `caption_en` finns bara där Commons-beskrivningen inte redan är engelsk.
 *
 * `file` är repo-rot-relativ under _generated (t.ex. "kommunkort/photos/0180/1.jpg");
 * getPhotos() prependar /data/_generated/ på samma sätt som kommun-wiki.js.
 *
 * Fail-open: saknad fil / saknad kommun → getters returnerar null/[] och
 * kortet degraderar (Wikipedia-hero + generiska upplevelser).
 *
 * Public API:
 *   ensureKommunkort()          — idempotent fetch av båda filerna, promise
 *   getKommunkortContent(kod)   — sync lookup: { landskap, lead, vibe, experiences } | null
 *   getKommunkortPhotos(kod)    — sync lookup: [ { url, urlSm, url1x, caption, credit, … } ]
 */

// ?v= bumpas när content.json byts ut i shared-mappen (JSON cachas kortare än
// JS/CSS, men inte kort nog för att en datauppdatering ska synas direkt).
const CONTENT_URL = '/data/_generated/kommunkort/content.json?v=en1';
const PHOTOS_URL   = '/data/_generated/kommunkort/photos.json?v=granskad2';
const ASSET_BASE   = '/data/_generated/';
// ⚠ Bilderna ligger på POSITIONELLA sökvägar (photos/<kod>/1.jpg) och servas
// immutable i ett år. Byts en kommuns urval ut pekar samma URL på ett ANNAT
// motiv, och Bunny-purgen vid promote tömmer bara edgen — inte klienternas
// disk-cache (CLAUDE.md §6). 2026-08-26 byttes 67 kommuners bilder ut i en
// omgranskning, alla på oförändrade filnamn. Bumpa den här när photos.json
// bumpas av samma skäl; annars sitter någon kvar med den underkända bilden.
// Samma dag (granskad2) OMORDNADES 284 kommuner när sorteringsbuggen lagades
// — då bytte i praktiken varje position motiv, inte bara de utbytta.
//
// ponytail: EN konstant för alla bilder — busta:r även de kommuner som inte
// ändrats. Systerpipelinen gör det bättre: `build_park_foton.py` stämplar
// en innehållshash PER FIL i url:en ("0.jpg?v=1eb9f07e"), så bara det som
// faktiskt bytts hämtas om. Uppgradera hit om omladdningen någonsin mäts som
// ett problem — det kräver att build_kommunkort_photos.py skriver hashen.
const ASSET_V      = '?v=granskad2';

let contentCache = null;   // { kod: entry } | null (innan fetch klar)
let photosCache  = null;
let inflight     = null;

export function ensureKommunkort() {
    if (contentCache && photosCache) return Promise.resolve();
    if (inflight) return inflight;
    const fetchJson = (url) => fetch(url)
        .then((r) => (r && r.ok ? r.json() : null))
        .then((d) => (d && typeof d === 'object' ? d : {}))
        .catch(() => ({}));
    inflight = Promise.all([fetchJson(CONTENT_URL), fetchJson(PHOTOS_URL)])
        .then(([content, photos]) => {
            contentCache = content;
            photosCache  = photos;
        });
    return inflight;
}

// content.json är skriven på SVENSKA; översättningar ligger som `<fält>_<lang>`
// (t.ex. `lead_en`, `vibe_en`). Resolveras här vid läsning i stället för i
// korten — båda korten re-renderar ändå vid språkbyte, och då slipper de veta
// att suffixen finns. Saknas översättningen faller fältet tillbaka på svenskan.
// (`experiences[].note` går INTE den här vägen — den hanteras av expNote() i
// kommunkort-experiences.js, som byter till den generiska översatta noten.)
const LOCALIZED_FIELDS = ['lead', 'vibe'];

function currentLocale() {
    const i = (typeof window !== 'undefined') ? window.i18n : null;
    return (i && i.getLocale) ? i.getLocale() : 'sv';
}

function localizeEntry(entry) {
    const loc = currentLocale();
    if (loc === 'sv') return entry;
    let out = entry;
    for (const f of LOCALIZED_FIELDS) {
        const v = entry[f + '_' + loc];
        const has = Array.isArray(v) ? v.length > 0 : (typeof v === 'string' && v);
        if (!has) continue;
        if (out === entry) out = Object.assign({}, entry);
        out[f] = v;
    }
    return out;
}

export function getKommunkortContent(kod) {
    if (!contentCache || !kod) return null;
    const entry = contentCache[kod];
    return entry ? localizeEntry(entry) : null;
}

export function getKommunkortPhotos(kod) {
    if (!photosCache || !kod) return [];
    const list = photosCache[kod];
    if (!Array.isArray(list)) return [];
    // Bara giltiga file-strängar; bygg absolut URL. credit-fälten kan saknas.
    // `caption_en` finns bara där originaltexten INTE redan är engelsk (Commons-
    // beskrivningarna är blandade språk) → fallback till `caption`. `credit`,
    // `credit_href` och `license` översätts ALDRIG — det är attribution.
    const loc = currentLocale();
    return list
        .filter((p) => p && typeof p.file === 'string' && p.file)
        .map((p) => ({
            url:         ASSET_BASE + p.file + ASSET_V,
            // Galleri-variant (480 px, `build_kommunkort_thumbs.py`). Hero
            // använder ALLTID `url` — den renderas upp till 468 CSS-px bred,
            // alltså 1 404 px på en 3×-skärm, och 1 200-originalet är rätt
            // storlek där. Galleriet är ~150 CSS-px och fick åtta gånger för
            // många pixlar innan. Saknas fältet (äldre photos.json) faller
            // anroparen tillbaka på `url`.
            urlSm:       typeof p.file_sm === 'string' && p.file_sm
                             ? ASSET_BASE + p.file_sm + ASSET_V : '',
            // Hero-variant för 1×-skärmar (640 px). KOMPLETTERAR `url` i en
            // srcset — den ersätter den inte. På en vanlig 1×-desktop hämtades
            // annars 14 gånger fler pixlar än som visades.
            url1x:       typeof p.file_1x === 'string' && p.file_1x
                             ? ASSET_BASE + p.file_1x + ASSET_V : '',
            caption:     (loc !== 'sv' && typeof p['caption_' + loc] === 'string' && p['caption_' + loc])
                             || (typeof p.caption === 'string' ? p.caption : ''),
            credit:      typeof p.credit === 'string' ? p.credit : '',
            credit_href: typeof p.credit_href === 'string' ? p.credit_href : '',
            license:     typeof p.license === 'string' ? p.license : '',
        }));
}
